import type { GameState, Passenger } from '../types.js';

/**
 * Emergencies (ADR-0029): a crew called out to an incident — up to it, a while
 * there, back down with who they came for — on one clock that ends the run if
 * it runs out.
 *
 * Here are the questions the rest of the sim, the view and the bots ask about
 * them, so none of them has to work out what an emergency is on its own.
 */

/** One call-out, from the crew arriving to the return reaching its door. */
export interface Incident {
  /** The crew on the way up. */
  readonly crew: Passenger;
  /** The crew and who they came for, on the way down. */
  readonly back: Passenger;
  /** Where it is happening. */
  readonly floor: number;
  /**
   * Where it stands: the crew waiting for the lift or riding it up, at the
   * incident, or the return waiting or riding down.
   */
  readonly phase: 'arriving' | 'attending' | 'returning';
  /** While attending, how long until they are ready to come back. */
  readonly readyInMs: number;
  /** The clock: the moment the run ends if the return is not home. */
  readonly byMs: number | null;
}

/** Every call-out under way: the crew has arrived and the return is not home yet. */
export function incidents(state: GameState): Incident[] {
  const out: Incident[] = [];
  for (const back of state.passengers) {
    if (back.follows === null) continue;
    const crew = state.passengers.find((p) => p.id === back.follows);
    // Only an emergency: a window cleaner's trip to the roof is a call-out too
    // (ADR-0031), but nothing to stop the shift or light the building for.
    if (!crew?.critical || crew.state === 'unspawned') continue;
    if (back.state === 'delivered' || back.state === 'abandoned' || crew.state === 'abandoned') {
      continue;
    }
    const attending = crew.state === 'delivered' && back.state === 'unspawned';
    out.push({
      crew,
      back,
      floor: crew.destination,
      phase: back.state === 'unspawned' ? (attending ? 'attending' : 'arriving') : 'returning',
      readyInMs: attending ? Math.max(0, back.spawnMs - state.elapsedMs) : 0,
      byMs: back.deadlineMs,
    });
  }
  return out;
}

/** True while any call-out is under way: a shift does not end in the middle of one. */
export function emergencyUnderWay(state: GameState): boolean {
  return incidents(state).length > 0;
}

/** Someone whose loss ends the run has been lost. */
export function emergencyLost(state: GameState): boolean {
  return state.passengers.some((p) => p.critical && p.state === 'abandoned');
}

/** Start the return's clock: the crew has just reached the incident. */
export function callOutArrived(state: GameState, crew: Passenger): void {
  for (const p of state.passengers) {
    if (p.follows === crew.id && p.state === 'unspawned') {
      p.spawnMs = state.elapsedMs + p.followDelayMs;
    }
  }
}
